/**
 * Chat query operations - Update
 */

import { getSupabaseClient } from '../../supabase-client.js';
import type { StoredChat } from '../types.js';
import { toUserId } from '../../types.js';
import { logger } from '../../../logger.js';

/**
 * Update a chat
 */
export async function updateChat(
  chatId: string,
  updates: Partial<Omit<StoredChat, 'id' | 'user_id' | 'created_at'>>
): Promise<StoredChat> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    const { data, error } = await client
      .from('meridian_chats')
      .update({
        ...updates,
        updated_at: now,
      })
      .eq('id', chatId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data as StoredChat;
  } catch (error) {
    logger.error('error updating chat', error, { op: 'updateChat' });
    throw error;
  }
}

/**
 * Update chat status
 */
export async function updateChatStatus(
  chatId: string,
  status: 'active' | 'archived' | 'deleted'
): Promise<StoredChat> {
  return updateChat(chatId, { status });
}

/**
 * Update chat title
 */
export async function updateChatTitle(
  chatId: string,
  title: string
): Promise<StoredChat> {
  return updateChat(chatId, { title });
}

/**
 * Update chat last message timestamp
 */
export async function updateChatLastMessageAt(
  chatId: string,
  timestamp: string
): Promise<StoredChat> {
  return updateChat(chatId, { last_message_at: timestamp });
}

/**
 * Update chat owner (user_id).
 * Used when Slack user mapping changes - reassigns ownership so the current
 * authenticated user can access a chat they previously created under a different mapping.
 */
export async function updateChatUserId(
  chatId: string,
  userId: string
): Promise<StoredChat> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    const { data, error } = await client
      .from('meridian_chats')
      .update({
        user_id: toUserId(userId),
        updated_at: now,
      })
      .eq('id', chatId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data as StoredChat;
  } catch (error) {
    logger.error('error updating chat user id', error, { op: 'updateChatUserId' });
    throw error;
  }
}
